import React, { useEffect, useRef, useState } from "react";
import ReactFragment from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import config from "./legend.landing.json";

const preventLandingCopy = (e) => {
  e.preventDefault();
};

/** Voorkomt selecteren, slepen en (zoveel mogelijk) lang indrukken / contextmenu op decoratieve screenshots. */
function landingProtectedImgProps(extraStyle = {}) {
  return {
    draggable: false,
    onContextMenu: preventLandingCopy,
    onDragStart: preventLandingCopy,
    onCopy: preventLandingCopy,
    style: {
      userSelect: "none",
      WebkitUserSelect: "none",
      MozUserSelect: "none",
      msUserSelect: "none",
      WebkitTouchCallout: "none",
      WebkitUserDrag: "none",
      ...extraStyle,
    },
  };
}

const landingNoCopyContainerStyle = {
  userSelect: "none",
  WebkitUserSelect: "none",
  WebkitTouchCallout: "none",
};

// svg tile 3.svg: orbit center and per-orb centers for scroll animation (viewBox 0 0 2409 2409)
const ORB_CENTER_X = 1204;
const ORB_CENTER_Y = 1188;
const MAX_ORB_ANGLE = 72;
const ORB_CENTERS = [[699, 203], [1974, 671], [616, 1036], [1505, 2065], [278, 1763]];

// Tile 3 with scroll-based orb rotation (light bulbs stay upright).
function SvgTile3Animated() {
  const containerRef = useRef(null);
  const objectRef = useRef(null);

  useEffect(() => {
    const applyOrbRotation = (angle) => {
      const obj = objectRef.current;
      if (!obj?.contentDocument) return;
      const svg = obj.contentDocument.querySelector("svg");
      if (!svg) return;
      ORB_CENTERS.forEach(([cx, cy], i) => {
        const g = svg.querySelector(`#orb${i + 1}`);
        if (g) g.setAttribute("transform", `rotate(${angle} ${ORB_CENTER_X} ${ORB_CENTER_Y}) rotate(${-angle} ${cx} ${cy})`);
      });
    };

    const onScroll = () => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const sectionCenterY = rect.top + rect.height / 2;
      const viewportCenter = vh / 2;
      let progress = 0;
      if (rect.top >= vh) progress = 0;
      else if (sectionCenterY <= viewportCenter) progress = 1;
      else progress = (vh - rect.top) / (vh / 2 + rect.height / 2);
      progress = Math.max(0, Math.min(1, progress));
      const angle = progress * MAX_ORB_ANGLE;
      applyOrbRotation(angle);
    };

    const onLoad = () => {
      const svg = obj?.contentDocument?.querySelector("svg");
      if (svg) svg.setAttribute("preserveAspectRatio", "xMidYMid slice");
      onScroll();
    };
    const obj = objectRef.current;
    if (obj) {
      obj.addEventListener("load", onLoad);
      if (obj.contentDocument) {
        const svg = obj.contentDocument.querySelector("svg");
        if (svg) svg.setAttribute("preserveAspectRatio", "xMidYMid slice");
        onScroll();
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (obj) obj.removeEventListener("load", onLoad);
    };
  }, []);

  return (
    <div ref={containerRef} style={{ position: "absolute", inset: 0, borderRadius: 28, overflow: "hidden", transform: "scale(0.97)", transformOrigin: "center center" }}>
      <object
        ref={objectRef}
        data="/svg%20tile%203.svg"
        type="image/svg+xml"
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          top: 0,
          left: 0,
          objectFit: "cover",
          objectPosition: "center center"
        }}
        aria-label=""
      />
    </div>
  );
}

// Tile 1 (left): right-hand form box moves up/down slightly with scroll. No opacity change.
const TILE1_BOX_MAX_OFFSET = 18; // max pixels the right box moves

function SvgTile1WithScroll() {
  const containerRef = useRef(null);
  const objectRef = useRef(null);

  useEffect(() => {
    const applyBoxOffset = () => {
      const el = containerRef.current;
      const obj = objectRef.current;
      if (!el?.getBoundingClientRect || !obj?.contentDocument) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const viewportCenterY = vh / 2;
      const sectionCenterY = rect.top + rect.height / 2;
      // Scroll down (section moves up) -> box moves up (negative translateY)
      let dy = viewportCenterY - sectionCenterY;
      dy = Math.max(-TILE1_BOX_MAX_OFFSET, Math.min(TILE1_BOX_MAX_OFFSET, dy * 0.08));
      const svg = obj.contentDocument.querySelector("svg");
      const box = svg?.querySelector("#tile1-right-box");
      if (box) box.setAttribute("transform", `translate(0, ${dy})`);
    };

    const onLoad = () => {
      const svg = obj?.contentDocument?.querySelector("svg");
      if (svg) svg.setAttribute("preserveAspectRatio", "xMidYMid slice");
      applyBoxOffset();
    };
    const obj = objectRef.current;
    if (obj) {
      obj.addEventListener("load", onLoad);
      if (obj.contentDocument) {
        const svg = obj.contentDocument.querySelector("svg");
        if (svg) svg.setAttribute("preserveAspectRatio", "xMidYMid slice");
        applyBoxOffset();
      }
    }
    window.addEventListener("scroll", applyBoxOffset, { passive: true });
    window.addEventListener("resize", applyBoxOffset);
    applyBoxOffset();
    return () => {
      window.removeEventListener("scroll", applyBoxOffset);
      window.removeEventListener("resize", applyBoxOffset);
      if (obj) obj.removeEventListener("load", onLoad);
    };
  }, []);

  return (
    <div ref={containerRef} style={{ position: "absolute", inset: 0, borderRadius: 28, overflow: "hidden", transform: "scale(0.97)", transformOrigin: "center center" }}>
      <object
        ref={objectRef}
        data="/svg%20tile%201.svg"
        type="image/svg+xml"
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          top: 0,
          left: 0,
          objectFit: "cover",
          objectPosition: "center center"
        }}
        aria-label=""
      />
    </div>
  );
}

// Summary tile: inject subtle floating animation for cursor and name indicators inside the SVG.
function SummaryTileAnimated() {
  const objectRef = useRef(null);

  useEffect(() => {
    const injectFloatAnimation = () => {
      const obj = objectRef.current;
      if (!obj?.contentDocument) return;
      const doc = obj.contentDocument;
      const root = doc.documentElement;
      const name1 = doc.getElementById("summary-name-1");
      const name2 = doc.getElementById("summary-name-2");
      if (!name1 && !name2) return;
      if (doc.getElementById("summary-tile-float-style")) return;
      const style = doc.createElement("style");
      style.id = "summary-tile-float-style";
      // Cursor + JJ pill use SMIL animation in SVG; name circles get CSS float
      style.textContent = `
        @keyframes summary-float {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(6px, -5px); }
          50% { transform: translate(-4px, 6px); }
          75% { transform: translate(5px, 4px); }
        }
        @keyframes summary-float-alt {
          0%, 100% { transform: translate(0, 0); }
          33% { transform: translate(-6px, 4px); }
          66% { transform: translate(5px, -5px); }
        }
        #summary-name-1 { animation: summary-float 5s ease-in-out infinite 0.3s; }
        #summary-name-2 { animation: summary-float-alt 4.5s ease-in-out infinite 0.6s; }
      `;
      if (root) root.appendChild(style);
    };

    const onLoad = () => {
      injectFloatAnimation();
      // Retry once after a short delay in case SVG DOM wasn't ready
      setTimeout(injectFloatAnimation, 100);
    };
    const obj = objectRef.current;
    if (obj) {
      obj.addEventListener("load", onLoad);
      if (obj.contentDocument?.documentElement) injectFloatAnimation();
    }
    return () => {
      if (obj) obj.removeEventListener("load", onLoad);
    };
  }, []);

  return (
    <div
      style={{ position: "absolute", inset: 0, overflow: "hidden", ...landingNoCopyContainerStyle }}
      onContextMenu={preventLandingCopy}
    >
      <object
        ref={objectRef}
        data="/Summary%20tile.svg"
        type="image/svg+xml"
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          top: 0,
          left: 0,
          objectFit: "cover",
          objectPosition: "center center",
          pointerEvents: "none",
        }}
        aria-label=""
      />
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          ...landingNoCopyContainerStyle,
        }}
        onContextMenu={preventLandingCopy}
      />
    </div>
  );
}

// Renders one of the three tile SVGs. Tile 1 uses SvgTile1WithScroll (right box parallax); tile 3 uses SvgTile3Animated (orbs).
function SvgTile({ tile }) {
  if (tile === 1) return <SvgTile1WithScroll />;
  if (tile === 3) return <SvgTile3Animated />;
  return (
    <div style={{ position: "absolute", inset: 0, borderRadius: 28, overflow: "hidden", transform: "scale(0.97)", transformOrigin: "center center" }}>
      <img
        src="/svg%20tile%202.svg"
        alt=""
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          top: 0,
          left: 0,
          objectFit: "cover",
          objectPosition: "center center",
          display: "block",
          borderRadius: 28
        }}
      />
    </div>
  );
}

export default function LandingPage() {
  const { theme, sections, footer } = config;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [mobileTileSvgs, setMobileTileSvgs] = useState({ 1: null, 2: null, 3: null });
  const [summaryTileMobSvg, setSummaryTileMobSvg] = useState(null);
  const [logoNameSvg, setLogoNameSvg] = useState(null);

  useEffect(() => {
    document.title = "Slimmer af - Home";
  }, []);

  // Stripe success-URL per ongeluk op / gezet → alsnog naar billing (zoals bij /billing?payment=success)
  useEffect(() => {
    if (searchParams.get("payment") === "success") {
      navigate("/billing?payment=success", { replace: true });
    }
  }, [searchParams, navigate]);

  // Load mobile tile SVGs inline voor scherpe weergave op mobiel (geen img-rasterization)
  useEffect(() => {
    const urls = [
      "/svg%20tile%201%20mob.svg",
      "/svg%20tile%202%20mob.svg",
      "/svg%20tile%203%20mob.svg"
    ];
    urls.forEach((url, i) => {
      fetch(url)
        .then((r) => r.text())
        .then((text) => {
          const idx = i + 1;
          let scaled = text.replace(
            /<svg([^>]*)>/,
            (match) => {
              let attrs = match
                .replace(/\swidth="[^"]*"/, ' width="100%"')
                .replace(/\sheight="[^"]*"/, ' height="100%"');
              if (!/width=/.test(attrs)) attrs = attrs.replace(/<svg/, '<svg width="100%"');
              if (!/height=/.test(attrs)) attrs = attrs.replace(/<svg\s/, '<svg height="100%" ');
              return attrs.replace(/>$/, ' preserveAspectRatio="xMidYMid meet" style="display:block">');
            }
          );
          // Tile 1: root-rect heeft rx="50" in 3000 viewBox → visueel te klein; zet rx/ry op ~280 voor ~28px rounding
          if (idx === 1) {
            scaled = scaled.replace(
              /<rect\s+width="3000"\s+height="3000"\s+rx="50"\s/,
              '<rect width="3000" height="3000" rx="280" ry="280" '
            );
          }
          setMobileTileSvgs((prev) => ({ ...prev, [idx]: scaled }));
        })
        .catch(() => {});
    });
    // Summary tile mob –zelfde inline-aanpak voor scherpte; container met aspect 2116/2469 zodat beeld niet wordt gecropt
    fetch("/summary%20tile%20mob.svg")
      .then((r) => r.text())
      .then((text) => {
        const scaled = text.replace(
          /<svg([^>]*)>/,
          (match) => {
            let attrs = match
              .replace(/\swidth="[^"]*"/, ' width="100%"')
              .replace(/\sheight="[^"]*"/, ' height="100%"');
            if (!/width=/.test(attrs)) attrs = attrs.replace(/<svg/, '<svg width="100%"');
            if (!/height=/.test(attrs)) attrs = attrs.replace(/<svg\s/, '<svg height="100%" ');
            return attrs.replace(/>$/, ' preserveAspectRatio="xMidYMid meet" style="display:block">');
          }
        );
        setSummaryTileMobSvg(scaled);
      })
      .catch(() => {});
    // Logo + name.svg inline voor scherpe weergave op mobiel (header + menu)
    fetch("/logo-name.svg")
      .then((r) => r.text())
      .then((text) => {
        const scaled = text.replace(
          /<svg([^>]*)>/,
          (match) => {
            let attrs = match
              .replace(/\swidth="[^"]*"/, ' width="100%"')
              .replace(/\sheight="[^"]*"/, ' height="100%"');
            if (!/width=/.test(attrs)) attrs = attrs.replace(/<svg/, '<svg width="100%"');
            if (!/height=/.test(attrs)) attrs = attrs.replace(/<svg\s/, '<svg height="100%" ');
            return attrs.replace(/>$/, ' preserveAspectRatio="xMidYMid meet" style="display:block">');
          }
        );
        setLogoNameSvg(scaled);
      })
      .catch(() => {});
  }, []);

  // Scroll listener ref only (no state updates during scroll to avoid re-renders and flicker)
  const lastScrollYRef = useRef(0);
  useEffect(() => {
    let rafId = null;
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        lastScrollYRef.current = window.scrollY;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // Get all section titles for menu
  const menuItems = sections
    .filter(s => s.id && s.title)
    .map(s => ({
      label: s.title || s.id.charAt(0).toUpperCase() + s.id.slice(1),
      href: `#${s.id}`
    }));

  // Fixed margins that maintain consistent spacing regardless of zoom
  // Using viewport units for side margins to ensure consistent fit-to-width behavior
  const HERO_TOP_MARGIN = 32;
  const HERO_SIDE_MARGIN_VW = 1.2; // 1.2vw for responsive side margins

  const Section = ({ section }) => {
    const videoRowRef = useRef(null);
    const [videoRowWidth, setVideoRowWidth] = useState(null);
    const [mobileVideoReady, setMobileVideoReady] = useState(false);
    const [desktopVideoLeftReady, setDesktopVideoLeftReady] = useState(false);
    const [desktopVideoRightReady, setDesktopVideoRightReady] = useState(false);

    useEffect(() => {
      if (section.type !== "hero") return;
      const el = videoRowRef.current;
      if (!el) return;
      const measure = () => setVideoRowWidth(el.offsetWidth);
      measure();
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }, [section.type]);

    switch (section.type) {
      case "hero":
        return (
          <>
            {/* PC only: very light blue gradient top pane + top menu (Draftr-style) */}
            <section id={section.id} className="landing-hero-desktop-section" style={{
              padding: 0,
              background: "linear-gradient(180deg, #ffffff 0%, #ffffff 8%, #e0efff 22%, #cce0ff 38%, #e0efff 52%, #ffffff 68%, #ffffff 100%)",
              color: "#1f2937",
              minHeight: "100vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              overflow: "visible"
            }}>
              {/* Top menu bar - dark text on light gradient */}
              <header style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "20px clamp(24px, 4vw, 48px)",
                flexWrap: "wrap",
                gap: 16
              }}>
                <Link to="/" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
                  <img src="/logo-name.svg" alt="Slimmer af" style={{ height: 36, width: "auto", display: "block" }} />
                </Link>
                <nav style={{ display: "flex", alignItems: "center", gap: "clamp(20px, 3vw, 40px)", flexWrap: "wrap" }}>
                  <a href="#gallery" onClick={(e) => { e.preventDefault(); document.getElementById("gallery")?.scrollIntoView({ behavior: "smooth", block: "start" }); }} style={{ color: "#4b5563", textDecoration: "none", fontSize: 15, fontWeight: 500 }}>Zo werkt het</a>
                  <a href="#gallery" onClick={(e) => { e.preventDefault(); document.getElementById("gallery")?.scrollIntoView({ behavior: "smooth", block: "start" }); }} style={{ color: "#4b5563", textDecoration: "none", fontSize: 15, fontWeight: 500 }}>Product</a>
                  <a href="#faq" onClick={(e) => { e.preventDefault(); document.getElementById("faq")?.scrollIntoView({ behavior: "smooth", block: "start" }); }} style={{ color: "#4b5563", textDecoration: "none", fontSize: 15, fontWeight: 500 }}>FAQ</a>
                  <a href="#pricing" onClick={(e) => { e.preventDefault(); document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth", block: "start" }); }} style={{ color: "#4b5563", textDecoration: "none", fontSize: 15, fontWeight: 500 }}>Pricing</a>
                  <Link to="/login" style={{ color: "#4b5563", textDecoration: "none", fontSize: 15, fontWeight: 500 }}>Sign in</Link>
                  <Link to="/signup" style={{ padding: "10px 20px", borderRadius: 9999, background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)", color: "#FFFFFF", textDecoration: "none", fontSize: 15, fontWeight: 600, border: "none", boxShadow: "0 2px 8px rgba(51,153,255,0.3)" }}>Get started</Link>
                </nav>
              </header>
              {/* Hero content: title, subtext, CTA - no flex:1 so section height follows content and page can scroll */}
              <div style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px clamp(24px, 4vw, 48px) 80px",
                textAlign: "center",
                overflow: "visible"
              }}>
                <h1 style={{
                  fontSize: "clamp(2.75rem, 7vw, 4rem)",
                  lineHeight: 1.15,
                  margin: 0,
                  color: "#1f2937",
                  letterSpacing: "-0.02em",
                  fontWeight: 700,
                  maxWidth: "min(90vw, 720px)"
                }}>
                  Los jouw studielening{" "}
                  <span style={{
                    textDecoration: "underline",
                    textDecorationColor: "#3399FF",
                    textDecorationThickness: "3px",
                    textUnderlineOffset: "6px"
                  }}>
                    slimmer af
                  </span>
                </h1>
                <p style={{
                  fontSize: "clamp(16px, 2vw, 18px)",
                  color: "#4b5563",
                  marginTop: 20,
                  marginBottom: 0,
                  lineHeight: 1.6,
                  maxWidth: "min(90vw, 640px)"
                }}>
                  {section.subtitle}
                </p>
                <Link
                  to="/signup"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 32,
                    padding: "14px 28px",
                    borderRadius: 9999,
                    background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)",
                    color: "#FFFFFF",
                    textDecoration: "none",
                    fontSize: 16,
                    fontWeight: 600,
                    boxShadow: "0 8px 24px rgba(51,153,255,0.35)",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 12px 28px rgba(51,153,255,0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 8px 24px rgba(51,153,255,0.35)";
                  }}
                >
                  Get started
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                </Link>

                {/* Video row:zelfde hoogte, originele verhouding, groep gecentreerd */}
                <div style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  marginTop: 80,
                  paddingLeft: "clamp(24px, 4vw, 48px)",
                  paddingRight: "clamp(24px, 4vw, 48px)",
                  boxSizing: "border-box"
                }}>
                  <div
                    ref={videoRowRef}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 48,
                      flexWrap: "wrap",
                      justifyContent: "center"
                    }}
                  >
                    {/* Linkervideo + caption */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                      <div
                        onContextMenu={preventLandingCopy}
                        style={{
                          height: "min(560px, 55vh)",
                          width: "fit-content",
                          borderRadius: 20,
                          overflow: "hidden",
                          boxShadow: "0 24px 48px rgba(0,0,0,0.12), 0 12px 24px rgba(0,0,0,0.08)",
                          background: "#f3f4f6",
                          border: "1px solid #e5e7eb",
                          position: "relative",
                          ...landingNoCopyContainerStyle,
                        }}
                      >
                        {!desktopVideoLeftReady && (
                          <div style={{
                            position: "absolute", inset: 0, zIndex: 1,
                            background: "linear-gradient(135deg, #e0efff 0%, #cce0ff 100%)",
                            display: "flex", alignItems: "center", justifyContent: "center"
                          }}>
                            <div style={{
                              width: 32, height: 32,
                              border: "3px solid rgba(51,153,255,0.25)",
                              borderTopColor: "#3399FF",
                              borderRadius: "50%",
                              animation: "spin 0.8s linear infinite"
                            }} />
                          </div>
                        )}
                        <img
                          src="/Static%20Portal%20PC.png"
                          alt="Slimmer af portal op desktop"
                          onLoad={() => setDesktopVideoLeftReady(true)}
                          onError={() => setDesktopVideoLeftReady(true)}
                          {...landingProtectedImgProps({
                            height: "100%",
                            width: "auto",
                            display: "block",
                            pointerEvents: "none",
                            opacity: desktopVideoLeftReady ? 1 : 0,
                            transition: "opacity 0.4s ease-out",
                          })}
                        />
                        {desktopVideoLeftReady && (
                          <div
                            style={{
                              position: "absolute",
                              left: "66%",
                              top: "69%",
                              transform: "translate(-50%, -50%)",
                              width: "6%",
                              zIndex: 2,
                              pointerEvents: "none",
                            }}
                            aria-hidden="true"
                          >
                            <img
                              src="/Mouse%20segment.svg"
                              alt=""
                              className="mouse-segment-float"
                              {...landingProtectedImgProps({
                                width: "100%",
                                height: "auto",
                                display: "block",
                              })}
                            />
                          </div>
                        )}
                      </div>
                      <span style={{ fontWeight: 700, fontSize: 30, color: "#374151" }}>Desktop</span>
                    </div>
                    {/* Rechterkolom: statische mobiele portal-afbeelding (zelfde kader als links) */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                      <div
                        onContextMenu={preventLandingCopy}
                        style={{
                          height: "min(560px, 55vh)",
                          width: "fit-content",
                          borderRadius: 20,
                          overflow: "hidden",
                          boxShadow: "0 24px 48px rgba(0,0,0,0.12), 0 12px 24px rgba(0,0,0,0.08)",
                          background: "#f3f4f6",
                          border: "1px solid #e5e7eb",
                          position: "relative",
                          ...landingNoCopyContainerStyle,
                        }}
                      >
                        {!desktopVideoRightReady && (
                          <div style={{
                            position: "absolute", inset: 0, zIndex: 1,
                            background: "linear-gradient(135deg, #e0efff 0%, #cce0ff 100%)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            <div style={{
                              width: 32, height: 32,
                              border: "3px solid rgba(51,153,255,0.25)",
                              borderTopColor: "#3399FF",
                              borderRadius: "50%",
                              animation: "spin 0.8s linear infinite"
                            }} />
                          </div>
                        )}
                        <img
                          src="/Static%20Portal%20Mobile.png"
                          alt="Slimmer af portal op mobiel"
                          onLoad={() => setDesktopVideoRightReady(true)}
                          onError={() => setDesktopVideoRightReady(true)}
                          {...landingProtectedImgProps({
                            height: "100%",
                            width: "auto",
                            display: "block",
                            pointerEvents: "none",
                            opacity: desktopVideoRightReady ? 1 : 0,
                            transition: "opacity 0.4s ease-out",
                          })}
                        />
                      </div>
                      <span style={{ fontWeight: 700, fontSize: 30, color: "#374151" }}>Mobiel</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content-wrapper: toolbox 3 tiles bereiken exact buitenranden van video row */}
              <div style={{
                width: "100%",
                margin: "0 auto",
                padding: "80px clamp(24px, 4vw, 48px) 0",
                boxSizing: "border-box"
              }}>
              <div id="toolbox" style={{
                width: "100%",
                padding: "0 0 16px 0",
                textAlign: "center"
              }}>
                <h2 style={{
                  margin: "0 0 16px 0",
                  fontSize: "clamp(1.75rem, 4vw, 2.5rem)",
                  fontWeight: 700,
                  color: "#1f2937",
                  lineHeight: 1.2
                }}>
                  Jouw toolbox om je aflosstrategie
                  <br />
                  op te stellen en bij te houden
                </h2>
                <p style={{
                  margin: "0 0 48px 0",
                  fontSize: "clamp(15px, 2vw, 17px)",
                  color: "#4b5563",
                  lineHeight: 1.6,
                  maxWidth: 640,
                  marginLeft: "auto",
                  marginRight: "auto"
                }}>
                  Stel je plan samen, sla het op en pas het aan wanneer je situatie verandert – in één overzichtelijke omgeving.
                </p>
                <div style={{ width: videoRowWidth ?? "100%", maxWidth: "100%", margin: "0 auto" }}>
                <div className="landing-grid-3" style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 12,
                  alignItems: "stretch",
                  textAlign: "left"
                }}>
                  {/* Card 1 – PC: smallere gap = bredere kaarten; container + inner minder padding = grotere afbeelding; tekst meer ruimte boven */}
                  <div style={{
                    background: "#fff",
                    borderRadius: 28,
                    overflow: "hidden",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
                    border: "1px solid #e5e7eb",
                    display: "flex",
                    flexDirection: "column"
                  }}>
                    <div style={{ width: "100%", aspectRatio: "4/3", position: "relative", background: "#fff", borderRadius: "28px 28px 0 0", padding: 2, boxSizing: "border-box" }}>
                      <div style={{ position: "absolute", top: 2, left: 2, right: 2, bottom: 2, borderRadius: 28, overflow: "hidden" }}>
                        <SvgTile tile={1} />
                      </div>
                    </div>
                    <div style={{ padding: "14px 14px 16px 14px" }}>
                      <h3 style={{ margin: "0 0 6px 0", fontSize: 17, fontWeight: 700, color: "#1f2937" }}>Stel een aflosstrategie samen die bij jou past</h3>
                      <p style={{ margin: 0, fontSize: 14, color: "#6b7280", lineHeight: 1.5 }}>Vul je gegevens in, kies je maandbedrag en zie direct het effect op je aflossing.</p>
                    </div>
                  </div>
                  {/* Card 2 */}
                  <div style={{
                    background: "#fff",
                    borderRadius: 28,
                    overflow: "hidden",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
                    border: "1px solid #e5e7eb",
                    display: "flex",
                    flexDirection: "column"
                  }}>
                    <div style={{ width: "100%", aspectRatio: "4/3", position: "relative", background: "#fff", borderRadius: "28px 28px 0 0", padding: 2, boxSizing: "border-box" }}>
                      <div style={{ position: "absolute", top: 2, left: 2, right: 2, bottom: 2, borderRadius: 28, overflow: "hidden" }}>
                        <SvgTile tile={2} />
                      </div>
                    </div>
                    <div style={{ padding: "14px 14px 16px 14px" }}>
                      <h3 style={{ margin: "0 0 6px 0", fontSize: 17, fontWeight: 700, color: "#1f2937" }}>Sla strategieën op en begin met jouw plan</h3>
                      <p style={{ margin: 0, fontSize: 14, color: "#6b7280", lineHeight: 1.5 }}>Bewaar je scenario's en ga aan de slag met het plan dat bij je past.</p>
                    </div>
                  </div>
                  {/* Card 3 */}
                  <div style={{
                    background: "#fff",
                    borderRadius: 28,
                    overflow: "hidden",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
                    border: "1px solid #e5e7eb",
                    display: "flex",
                    flexDirection: "column"
                  }}>
                    <div style={{ width: "100%", aspectRatio: "4/3", position: "relative", background: "#fff", borderRadius: "28px 28px 0 0", padding: 2, boxSizing: "border-box" }}>
                      <div style={{ position: "absolute", top: 2, left: 2, right: 2, bottom: 2, borderRadius: 28, overflow: "hidden" }}>
                        <SvgTile tile={3} />
                      </div>
                    </div>
                    <div style={{ padding: "14px 14px 16px 14px" }}>
                      <h3 style={{ margin: "0 0 6px 0", fontSize: 17, fontWeight: 700, color: "#1f2937" }}>Kom terug en stel je strategie bij</h3>
                      <p style={{ margin: 0, fontSize: 14, color: "#6b7280", lineHeight: 1.5 }}>Je situatie verandert. Log in en pas je aflosplan eenvoudig aan wanneer het nodig is.</p>
                    </div>
                  </div>
                </div>
                </div>
              </div>

              <div id="hoe-slimmer-af" style={{
                width: "100%",
                marginTop: "clamp(2px, 0.5vw, 6px)",
                background: "transparent"
              }}>
                <div style={{ width: videoRowWidth ?? "100%", maxWidth: "100%", margin: "0 auto", boxSizing: "border-box" }}>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 0.5fr) minmax(0, 1fr)",
                  gridTemplateRows: "auto",
                  gap: "clamp(32px, 5vw, 64px)",
                  alignItems: "stretch",
                  width: "100%"
                }}>
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gridColumn: 1,
                    gridRow: 1
                  }}>
                    <h2 style={{
                      margin: "0 0 clamp(24px, 3.5vw, 36px) 0",
                      fontSize: "clamp(1.5rem, 4vw, 2.25rem)",
                      fontWeight: 700,
                      color: "#1f2937",
                      lineHeight: 1.2,
                      textAlign: "left"
                    }}>
                      Stel een plan samen dat bij jou past
                    </h2>
                    <div style={{ display: "flex", flexDirection: "column", gap: "clamp(22px, 3vw, 36px)", textAlign: "left" }}>
                      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                        <span style={{
                          flexShrink: 0,
                          width: 42,
                          height: 42,
                          borderRadius: 10,
                          background: "#e5e7eb",
                          color: "#1f2937",
                          fontWeight: 700,
                          fontSize: 15,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}>01</span>
                        <div>
                          <h3 style={{ margin: "0 0 6px 0", fontSize: "clamp(15px, 1.8vw, 17px)", fontWeight: 700, color: "#1f2937" }}>Open een nieuwe strategie</h3>
                          <p style={{ margin: 0, fontSize: "clamp(14px, 1.5vw, 16px)", color: "#4b5563", lineHeight: 1.55 }}>Maak een nieuw plan of importeer gegevens met één klik. Stel je werkruimte moeiteloos in.</p>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                        <span style={{
                          flexShrink: 0,
                          width: 42,
                          height: 42,
                          borderRadius: 10,
                          background: "#e5e7eb",
                          color: "#1f2937",
                          fontWeight: 700,
                          fontSize: 15,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}>02</span>
                        <div>
                          <h3 style={{ margin: "0 0 6px 0", fontSize: "clamp(15px, 1.8vw, 17px)", fontWeight: 700, color: "#1f2937" }}>Stel makkelijk samen</h3>
                          <p style={{ margin: 0, fontSize: "clamp(14px, 1.5vw, 16px)", color: "#4b5563", lineHeight: 1.55 }}>Gebruik onze intuïtieve editor en slimme tools om je aflosstrategie helder in te richten.</p>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                        <span style={{
                          flexShrink: 0,
                          width: 42,
                          height: 42,
                          borderRadius: 10,
                          background: "#e5e7eb",
                          color: "#1f2937",
                          fontWeight: 700,
                          fontSize: 15,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}>03</span>
                        <div>
                          <h3 style={{ margin: "0 0 6px 0", fontSize: "clamp(15px, 1.8vw, 17px)", fontWeight: 700, color: "#1f2937" }}>Sla op en ga aan de slag</h3>
                          <p style={{ margin: 0, fontSize: "clamp(14px, 1.5vw, 16px)", color: "#4b5563", lineHeight: 1.55 }}>Bewaar je plan en pas het aan wanneer je situatie verandert. Alles overzichtelijk op één plek.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ position: "sticky", top: 24, gridColumn: 2, gridRow: 1, display: "flex", justifyContent: "flex-end" }}>
                    <div
                      onContextMenu={preventLandingCopy}
                      style={{
                        borderRadius: 20,
                        overflow: "hidden",
                        position: "relative",
                        width: "100%",
                        maxWidth: "95%",
                        aspectRatio: "3963 / 5463",
                        ...landingNoCopyContainerStyle,
                      }}
                    >
                      <SummaryTileAnimated />
                      <div
                        style={{
                          position: "absolute",
                          left: "66%",
                          top: "69%",
                          transform: "translate(-50%, -50%)",
                          width: "6%",
                          pointerEvents: "none"
                        }}
                        aria-hidden="true"
                      >
                        <img
                          src="/Mouse%20segment.svg"
                          alt=""
                          className="mouse-segment-float"
                          {...landingProtectedImgProps({
                            width: "100%",
                            height: "auto",
                            display: "block",
                          })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              </div>

              </div>

            </section>

            {/* Mobile Hero – same setup as PC: gradient, title, subtitle, Get started, mobile video, toolbox, hoe slimmer af */}
            <section id={section.id + "-mobile"} className="landing-hero-mobile-section" style={{
              background: "linear-gradient(180deg, #ffffff 0%, #ffffff 8%, #e0efff 22%, #cce0ff 38%, #e0efff 52%, #ffffff 68%, #ffffff 100%)",
              color: "#1f2937",
              display: "none",
              flexDirection: "column",
              padding: 0,
              overflow: "visible"
            }}>
              {/* Hero block: title, subtitle, Get started – centered like PC, equal left/right */}
              <div style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                padding: "24px 24px 40px 24px",
                maxWidth: "100%",
                boxSizing: "border-box"
              }}>
                <h1 style={{
                  fontSize: "clamp(2rem, 7vw, 2.75rem)",
                  lineHeight: 1.15,
                  margin: 0,
                  color: "#1f2937",
                  letterSpacing: "-0.02em",
                  fontWeight: 700,
                  maxWidth: "min(90vw, 520px)"
                }}>
                  Los jouw studielening{" "}
                  <span style={{
                    textDecoration: "underline",
                    textDecorationColor: "#3399FF",
                    textDecorationThickness: "3px",
                    textUnderlineOffset: "6px"
                  }}>
                    slimmer af
                  </span>
                </h1>
                <p style={{
                  fontSize: "clamp(15px, 4vw, 17px)",
                  color: "#4b5563",
                  marginTop: 16,
                  marginBottom: 0,
                  lineHeight: 1.6,
                  maxWidth: "min(90vw, 480px)"
                }}>
                  {section.subtitle}
                </p>
                <Link
                  to="/portal-demo"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 28,
                    padding: "14px 26px",
                    borderRadius: 9999,
                    background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)",
                    color: "#FFFFFF",
                    textDecoration: "none",
                    fontSize: 16,
                    fontWeight: 600,
                    boxShadow: "0 8px 24px rgba(51,153,255,0.35)"
                  }}
                >
                  Get started
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                </Link>

                {/* Statische mobiele portal-afbeelding + zwevende muis (zelfde als summary-blok verderop) */}
                <div
                  onContextMenu={preventLandingCopy}
                  style={{
                    marginTop: 48,
                    width: "min(100%, 320px)",
                    aspectRatio: "9/16",
                    maxHeight: "min(70vh, 560px)",
                    borderRadius: 20,
                    overflow: "hidden",
                    boxShadow: "0 24px 48px rgba(0,0,0,0.12), 0 12px 24px rgba(0,0,0,0.08)",
                    background: "#f3f4f6",
                    border: "1px solid #e5e7eb",
                    position: "relative",
                    ...landingNoCopyContainerStyle,
                  }}
                >
                  {!mobileVideoReady && (
                    <div style={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 1,
                      background: "linear-gradient(135deg, #e0efff 0%, #cce0ff 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
                      <div style={{
                        width: 32,
                        height: 32,
                        border: "3px solid rgba(51,153,255,0.25)",
                        borderTopColor: "#3399FF",
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite"
                      }} />
                    </div>
                  )}
                  <img
                    src="/Static%20Portal%20Mobile.png"
                    alt="Slimmer af portal op mobiel"
                    onLoad={() => setMobileVideoReady(true)}
                    onError={() => setMobileVideoReady(true)}
                    {...landingProtectedImgProps({
                      width: "100%",
                      height: "100%",
                      display: "block",
                      objectFit: "cover",
                      objectPosition: "center center",
                      opacity: mobileVideoReady ? 1 : 0,
                      transition: "opacity 0.4s ease-out",
                    })}
                  />
                  {mobileVideoReady && (
                    <div
                      style={{
                        position: "absolute",
                        left: "66%",
                        top: "71%",
                        transform: "translate(-50%, -50%)",
                        width: "14%",
                        zIndex: 2,
                        pointerEvents: "none",
                      }}
                      aria-hidden="true"
                    >
                      <img
                        src="/Mouse%20segment.svg"
                        alt=""
                        className="mouse-segment-float"
                        {...landingProtectedImgProps({
                          width: "100%",
                          height: "auto",
                          display: "block",
                        })}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Toolbox – mobile: stacked cards, centered with equal left/right margins */}
              <div id="toolbox-mobile" style={{
                width: "100%",
                maxWidth: 600,
                margin: "0 auto",
                padding: "48px 24px 40px 24px",
                boxSizing: "border-box",
                textAlign: "center"
              }}>
                <h2 style={{
                  margin: "0 0 12px 0",
                  fontSize: "clamp(1.5rem, 5vw, 2rem)",
                  fontWeight: 700,
                  color: "#1f2937",
                  lineHeight: 1.2
                }}>
                  Jouw toolbox om je aflosstrategie
                  <br />
                  op te stellen en bij te houden
                </h2>
                <p style={{
                  margin: "0 0 32px 0",
                  fontSize: 15,
                  color: "#4b5563",
                  lineHeight: 1.6
                }}>
                  Stel je plan samen, sla het op en pas het aan wanneer je situatie verandert – in één overzichtelijke omgeving.
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 20, textAlign: "left" }}>
                  {/* Card 1 – inline SVG voor scherpe weergave; clip-path voor 28 rounding (SVG-tegel 1 heeft eigen achtergrond) */}
                  <div style={{
                    background: "#fff",
                    borderRadius: 28,
                    overflow: "hidden",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
                    border: "1px solid #e5e7eb"
                  }}>
                    <div style={{ width: "100%", aspectRatio: "1", position: "relative", background: "#fff", borderRadius: "28px 28px 0 0", padding: 6, boxSizing: "border-box", overflow: "hidden" }}>
                      <div
                        style={{
                          position: "absolute",
                          top: 6,
                          left: 6,
                          right: 6,
                          bottom: 6,
                          borderRadius: 28,
                          overflow: "hidden",
                          clipPath: "inset(0 round 28px)"
                        }}
                      >
                        {mobileTileSvgs[1] ? (
                          <div style={{ width: "100%", height: "100%" }} dangerouslySetInnerHTML={{ __html: mobileTileSvgs[1] }} />
                        ) : (
                          <div style={{ width: "100%", height: "100%", background: "#f3f4f6" }} />
                        )}
                      </div>
                    </div>
                    <div style={{ padding: "20px 20px 24px" }}>
                      <h3 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 700, color: "#1f2937" }}>Stel een aflosstrategie samen die bij jou past</h3>
                      <p style={{ margin: 0, fontSize: 14, color: "#6b7280", lineHeight: 1.5 }}>Vul je gegevens in, kies je maandbedrag en zie direct het effect op je aflossing.</p>
                    </div>
                  </div>
                  {/* Card 2 – inline SVG voor scherpe weergave op mobiel */}
                  <div style={{
                    background: "#fff",
                    borderRadius: 28,
                    overflow: "hidden",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
                    border: "1px solid #e5e7eb"
                  }}>
                    <div style={{ width: "100%", aspectRatio: "1", position: "relative", background: "#fff", borderRadius: "28px 28px 0 0", padding: 6, boxSizing: "border-box", overflow: "hidden" }}>
                      <div style={{ position: "absolute", top: 6, left: 6, right: 6, bottom: 6, borderRadius: 28, overflow: "hidden" }}>
                        {mobileTileSvgs[2] ? (
                          <div style={{ width: "100%", height: "100%" }} dangerouslySetInnerHTML={{ __html: mobileTileSvgs[2] }} />
                        ) : (
                          <div style={{ width: "100%", height: "100%", background: "#f3f4f6" }} />
                        )}
                      </div>
                    </div>
                    <div style={{ padding: "20px 20px 24px" }}>
                      <h3 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 700, color: "#1f2937" }}>Sla strategieën op en begin met jouw plan</h3>
                      <p style={{ margin: 0, fontSize: 14, color: "#6b7280", lineHeight: 1.5 }}>Bewaar je scenario's en ga aan de slag met het plan dat bij je past.</p>
                    </div>
                  </div>
                  {/* Card 3 – inline SVG voor scherpe weergave op mobiel */}
                  <div style={{
                    background: "#fff",
                    borderRadius: 28,
                    overflow: "hidden",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
                    border: "1px solid #e5e7eb"
                  }}>
                    <div style={{ width: "100%", aspectRatio: "1", position: "relative", background: "#fff", borderRadius: "28px 28px 0 0", padding: 6, boxSizing: "border-box", overflow: "hidden" }}>
                      <div style={{ position: "absolute", top: 6, left: 6, right: 6, bottom: 6, borderRadius: 28, overflow: "hidden" }}>
                        {mobileTileSvgs[3] ? (
                          <div style={{ width: "100%", height: "100%" }} dangerouslySetInnerHTML={{ __html: mobileTileSvgs[3] }} />
                        ) : (
                          <div style={{ width: "100%", height: "100%", background: "#f3f4f6" }} />
                        )}
                      </div>
                    </div>
                    <div style={{ padding: "20px 20px 24px" }}>
                      <h3 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 700, color: "#1f2937" }}>Kom terug en stel je strategie bij</h3>
                      <p style={{ margin: 0, fontSize: 14, color: "#6b7280", lineHeight: 1.5 }}>Je situatie verandert. Log in en pas je aflosplan eenvoudig aan wanneer het nodig is.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stel een plan samen dat bij jou past – mobile: zelfde inhoud als PC, stacked formaat */}
              <div id="hoe-slimmer-af-mobile" style={{
                width: "100%",
                maxWidth: 600,
                margin: "0 auto",
                padding: "40px 24px 56px 24px",
                boxSizing: "border-box",
                background: "transparent"
              }}>
                <h2 style={{
                  margin: "0 0 clamp(24px, 3.5vw, 36px) 0",
                  fontSize: "clamp(1.5rem, 4vw, 2.25rem)",
                  fontWeight: 700,
                  color: "#1f2937",
                  lineHeight: 1.2,
                  textAlign: "left"
                }}>
                  Stel een plan samen dat bij jou past
                </h2>
                <div style={{ display: "flex", flexDirection: "column", gap: "clamp(22px, 3vw, 36px)", textAlign: "left" }}>
                  <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                    <span style={{
                      flexShrink: 0,
                      width: 42,
                      height: 42,
                      borderRadius: 10,
                      background: "#e5e7eb",
                      color: "#1f2937",
                      fontWeight: 700,
                      fontSize: 15,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>01</span>
                    <div>
                      <h3 style={{ margin: "0 0 6px 0", fontSize: "clamp(15px, 1.8vw, 17px)", fontWeight: 700, color: "#1f2937" }}>Open een nieuwe strategie</h3>
                      <p style={{ margin: 0, fontSize: "clamp(14px, 1.5vw, 16px)", color: "#4b5563", lineHeight: 1.55 }}>Maak een nieuw plan of importeer gegevens met één klik. Stel je werkruimte moeiteloos in.</p>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                    <span style={{
                      flexShrink: 0,
                      width: 42,
                      height: 42,
                      borderRadius: 10,
                      background: "#e5e7eb",
                      color: "#1f2937",
                      fontWeight: 700,
                      fontSize: 15,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>02</span>
                    <div>
                      <h3 style={{ margin: "0 0 6px 0", fontSize: "clamp(15px, 1.8vw, 17px)", fontWeight: 700, color: "#1f2937" }}>Stel makkelijk samen</h3>
                      <p style={{ margin: 0, fontSize: "clamp(14px, 1.5vw, 16px)", color: "#4b5563", lineHeight: 1.55 }}>Gebruik onze intuïtieve editor en slimme tools om je aflosstrategie helder in te richten.</p>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                    <span style={{
                      flexShrink: 0,
                      width: 42,
                      height: 42,
                      borderRadius: 10,
                      background: "#e5e7eb",
                      color: "#1f2937",
                      fontWeight: 700,
                      fontSize: 15,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>03</span>
                    <div>
                      <h3 style={{ margin: "0 0 6px 0", fontSize: "clamp(15px, 1.8vw, 17px)", fontWeight: 700, color: "#1f2937" }}>Sla op en ga aan de slag</h3>
                      <p style={{ margin: 0, fontSize: "clamp(14px, 1.5vw, 16px)", color: "#4b5563", lineHeight: 1.55 }}>Bewaar je plan en pas het aan wanneer je situatie verandert. Alles overzichtelijk op één plek.</p>
                    </div>
                  </div>
                </div>
                {/* Summary tile mob – uitgelijnd met buitenranden, exacte verhouding 2116:2469; mouse segment zoals op PC */}
                <div
                  style={{
                    marginTop: 20,
                    marginLeft: -24,
                    marginRight: -24,
                    width: "calc(100% + 48px)",
                    aspectRatio: "2116 / 2469",
                    position: "relative",
                    overflow: "visible",
                    borderRadius: 28,
                    boxSizing: "border-box"
                  }}
                >
                  <div
                    style={{ position: "absolute", inset: 0, borderRadius: 28, overflow: "hidden", ...landingNoCopyContainerStyle }}
                    onContextMenu={preventLandingCopy}
                  >
                    {summaryTileMobSvg ? (
                      <div style={{ width: "100%", height: "100%", pointerEvents: "none" }} dangerouslySetInnerHTML={{ __html: summaryTileMobSvg }} />
                    ) : (
                      <div style={{ width: "100%", height: "100%", background: "#f3f4f6" }} />
                    )}
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      left: "66%",
                      top: "71%",
                      transform: "translate(-50%, -50%)",
                      width: "14%",
                      pointerEvents: "none"
                    }}
                    aria-hidden="true"
                  >
                    <img
                      src="/Mouse%20segment.svg"
                      alt=""
                      className="mouse-segment-float"
                      {...landingProtectedImgProps({
                        width: "100%",
                        height: "auto",
                        display: "block",
                      })}
                    />
                  </div>
                </div>
              </div>
            </section>
          </>
        );
      case "story":
        // Story section removed from design (DUO-schuld? Geen stress... / Jouw lening. Jouw tempo.)
        return null;
      case "investment":
        return <InvestmentSection section={section} />;
      case "marquee":
        return (
          <section id={section.id} className="landing-section-padding" style={{ padding: "24px 16px", background: "transparent" }}>
            <div style={{ maxWidth: 1024, margin: "0 auto", color: theme.colors.muted, display: "flex", gap: 24, flexWrap: "wrap", justifyContent: "center" }}>
              {section.items.map((item, idx) => (
                <span key={idx} style={{ opacity: 0.8, fontSize: "clamp(13px, 2vw, 16px)" }}>{item}</span>
              ))}
            </div>
          </section>
        );
      case "features":
        return (
          <section id={section.id} className="landing-section-padding" style={{ padding: "4px 24px", background: "transparent", color: theme.colors.foreground }}>
            <div className="landing-full-width-mobile" style={{ maxWidth: "1400px", margin: "0 auto" }}>
              <div className="landing-text-center-mobile" style={{ textAlign: "center", marginBottom: "60px" }}>
                <h2 style={{ fontSize: "clamp(1.5rem, 5vw, 2.5rem)", fontWeight: 700, marginBottom: "20px" }}>{section.title}</h2>
                {section.kicker && <p style={{ color: theme.colors.muted, fontSize: "clamp(16px, 2.5vw, 20px)", maxWidth: "900px", margin: "0 auto", lineHeight: 1.7 }}>{section.kicker}</p>}
              </div>
              <div className="landing-grid-auto-fit" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(350px, 100%), 1fr))", gap: "32px", maxWidth: "1300px", margin: "0 auto" }}>
                {section.items.map((f, idx) => (
                  <div key={idx} className="landing-feature-card" style={{ 
                    background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    border: "none", 
                    borderRadius: 20, 
                    padding: "24px", 
                    boxShadow: "0 12px 34px rgba(0,0,0,0.1)",
                    transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    backfaceVisibility: "hidden",
                    display: "flex",
                    flexDirection: "column"
                  }}
                  onMouseEnter={(e) => {
                    if (window.innerWidth > 768) {
                      e.currentTarget.style.transform = "translate3d(0, -8px, 0)";
                      e.currentTarget.style.boxShadow = "0 22px 52px rgba(51,153,255,0.28)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translate3d(0, 0, 0)";
                    e.currentTarget.style.boxShadow = "0 12px 34px rgba(0,0,0,0.1)";
                  }}>
                    {f.icon && (
                      <div style={{ 
                        width: 56,
                        height: 56,
                        background: "rgba(255,255,255,0.2)",
                        borderRadius: 14,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 0 16px 0",
                        fontSize: 30
                      }}>
                        {f.icon}
                      </div>
                    )}
                    <div style={{ fontWeight: 700, fontSize: "clamp(18px, 2.5vw, 20px)", marginBottom: 10, lineHeight: 1.3, color: "#FFFFFF" }}>{f.title}</div>
                    <div style={{ color: "rgba(255,255,255,0.9)", lineHeight: 1.5, fontSize: "clamp(15px, 2vw, 16px)" }}>{f.description}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      case "portal-preview":
        return (
          <section id={section.id} className="landing-section-padding" style={{ padding: "4px 24px", background: "transparent", color: theme.colors.foreground }}>
            <div className="landing-full-width-mobile" style={{ maxWidth: 1400, margin: "0 auto" }}>
              <div className="landing-text-center-mobile" style={{ textAlign: "center", marginBottom: 56 }}>
                <h2 style={{ fontSize: "clamp(1.5rem, 5vw, 3rem)", fontWeight: 700, marginBottom: 20 }}>{section.title}</h2>
                <p style={{ fontSize: "clamp(16px, 2.5vw, 22px)", color: theme.colors.muted, lineHeight: 1.7, maxWidth: 900, margin: "0 auto" }}>
                  {section.description}
                </p>
              </div>
              <div style={{ 
                borderRadius: 24, 
                overflow: "hidden", 
                boxShadow: "0 20px 60px rgba(0,0,0,0.15)"
              }}>
                <img 
                  src={section.image} 
                  alt={section.imageAlt} 
                  style={{ 
                    width: "100%", 
                    height: "auto", 
                    display: "block" 
                  }} 
                />
              </div>
            </div>
          </section>
        );
      case "report-preview":
        return (
          <section id={section.id} className="landing-section-padding" style={{ padding: "120px 24px", background: "transparent", color: theme.colors.foreground }}>
            <div className="landing-full-width-mobile" style={{ maxWidth: 1400, margin: "0 auto" }}>
              <div className="landing-text-center-mobile" style={{ textAlign: "center", marginBottom: 80 }}>
                <h2 style={{ fontSize: "clamp(1.5rem, 5vw, 3.5rem)", fontWeight: 700, marginBottom: 24 }}>{section.title}</h2>
                {section.subtitle && (
                  <p style={{ fontSize: "clamp(16px, 2.5vw, 24px)", color: theme.colors.muted, lineHeight: 1.7, maxWidth: 900, margin: "0 auto" }}>
                    {section.subtitle}
                  </p>
                )}
              </div>
              <div className="landing-grid-3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 48, maxWidth: 1400, margin: "0 auto" }}>
                {section.pages?.map((page, index) => (
                  <div key={index} style={{
                    textAlign: "center"
                  }}>
                    <div style={{ 
                      borderRadius: 24, 
                      overflow: "hidden", 
                      boxShadow: "0 20px 50px rgba(0,0,0,0.18)",
                      marginBottom: 28,
                      transition: "all 0.3s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-10px)";
                      e.currentTarget.style.boxShadow = "0 30px 70px rgba(0,0,0,0.25)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 20px 50px rgba(0,0,0,0.18)";
                    }}>
                      <img 
                        src={page.image} 
                        alt={page.title} 
                        style={{ 
                          width: "100%", 
                          height: "auto", 
                          display: "block" 
                        }} 
                      />
                    </div>
                    <h3 style={{ fontSize: "clamp(1.25rem, 3vw, 1.75rem)", fontWeight: 700, marginBottom: 14, color: theme.colors.foreground }}>
                      {page.title}
                    </h3>
                    <p style={{ fontSize: "clamp(15px, 2vw, 20px)", color: theme.colors.muted, lineHeight: 1.6, margin: 0 }}>
                      {page.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      case "pricing":
        return (
          <section id={section.id} className="landing-section-padding" style={{ padding: "40px 24px", background: "transparent", color: theme.colors.foreground }}>
            <div className="landing-full-width-mobile" style={{ maxWidth: 1000, margin: "0 auto" }}>
              <div className="landing-text-center-mobile" style={{ textAlign: "center", marginBottom: 56 }}>
                <h2 style={{ fontSize: "clamp(1.5rem, 5vw, 3rem)", fontWeight: 700, marginBottom: 28 }}>{section.title}</h2>
                {section.subtitle && (
                  <div style={{ 
                    fontSize: "clamp(18px, 2.5vw, 24px)", 
                    color: "#FFFFFF", 
                    lineHeight: 1.6, 
                    maxWidth: 900, 
                    margin: "0 auto",
                    fontWeight: 600,
                    background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)",
                    padding: "clamp(20px, 3vw, 28px) clamp(24px, 4vw, 40px)",
                    borderRadius: 20,
                    boxShadow: "0 20px 50px rgba(51,153,255,0.25)"
                  }}>
                    {section.subtitle}
                  </div>
                )}
              </div>
              <div style={{ 
                background: theme.colors.card, 
                border: `2px solid ${theme.colors.accent}`, 
                borderRadius: 24, 
                padding: "clamp(32px, 5vw, 56px)", 
                boxShadow: "0 25px 70px rgba(51,153,255,0.18)",
                textAlign: "center"
              }}>
                <div style={{ marginBottom: 40, display: "flex", flexDirection: "column", gap: 8 }}>
                  {section.compareAtPrice ? (
                    <div style={{ fontSize: "clamp(20px, 3vw, 28px)", fontWeight: 600, color: "#94A3B8", textDecoration: "line-through" }}>
                      {section.compareAtPrice}
                    </div>
                  ) : null}
                  <div style={{ fontSize: "clamp(2.5rem, 6vw, 4rem)", fontWeight: 700, color: theme.colors.accent }}>
                    {section.price}
                  </div>
                  <div style={{ fontSize: "clamp(16px, 2.5vw, 20px)", color: theme.colors.muted, fontWeight: 500 }}>
                    {section.period}
                  </div>
                </div>
                
                <ul style={{ 
                  listStyle: "none", 
                  padding: 0, 
                  margin: "0 0 40px 0",
                  textAlign: "left",
                  maxWidth: 500,
                  marginLeft: "auto",
                  marginRight: "auto"
                }}>
                  {section.features.map((ft, fIdx) => (
                    <li key={fIdx} style={{ 
                      padding: "14px 0", 
                      color: theme.colors.foreground,
                      fontSize: "clamp(16px, 2vw, 18px)",
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      lineHeight: 1.5
                    }}>
                      <span style={{ color: theme.colors.accent, fontSize: 24, fontWeight: 700 }}>✓</span>
                      {ft}
                    </li>
                  ))}
                </ul>
                
                <div style={{ width: "100%" }}>
                  {renderCta(section.cta, "primary")}
                </div>
              </div>
            </div>
          </section>
        );
      case "faq":
        return <FAQSection section={section} theme={theme} />;
      case "cta":
        return (
          <section id={section.id} className="landing-section-padding" style={{ 
            padding: "48px 24px", 
            textAlign: "center", 
            background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            borderRadius: "24px",
            margin: "40px auto 0",
            maxWidth: "800px",
            color: "#FFFFFF",
            position: "relative",
            overflow: "hidden",
            WebkitBackfaceVisibility: "hidden",
            backfaceVisibility: "hidden"
          }}>
            <div style={{ maxWidth: 720, margin: "0 auto", position: "relative", zIndex: 1 }}>
              <h2 style={{ fontSize: "clamp(1.5rem, 4vw, 2.25rem)", marginBottom: 12, color: "#FFFFFF" }}>{section.title}</h2>
              <p style={{ color: "rgba(255,255,255,0.9)", marginTop: 0, fontSize: "clamp(15px, 2vw, 18px)", lineHeight: 1.6 }}>{section.subtitle}</p>
              <div className="landing-hero-cta-container" style={{ display: "inline-flex", gap: 12, marginTop: 20, width: "100%" }}>
                {section.primaryCta && <div className="landing-hero-cta">{renderCta(section.primaryCta, "primary")}</div>}
                {section.secondaryCta && <div className="landing-hero-cta">{renderCta(section.secondaryCta, "ghost")}</div>}
              </div>
            </div>
          </section>
        );
      default:
        return null;
    }
  };

  const FAQSection = ({ section, theme }) => {
    const [openIndex, setOpenIndex] = useState(null);

    const toggleItem = (idx) => {
      setOpenIndex(openIndex === idx ? null : idx);
    };

    return (
      <section id={section.id} className="landing-section-padding" style={{ padding: "40px 24px", background: "transparent", color: theme.colors.foreground }}>
        <div className="landing-full-width-mobile" style={{ maxWidth: 800, margin: "0 auto" }}>
          <h2 style={{ fontSize: "clamp(1.5rem, 4vw, 2.25rem)", marginBottom: 24 }}>FAQ</h2>
          <div style={{ display: "grid", gap: 16 }}>
            {section.items.map((qa, idx) => {
              const isOpen = openIndex === idx;
              const hasDisclaimer = qa.a.includes("**BELANGRIJK:");
              const parts = hasDisclaimer ? qa.a.split("**BELANGRIJK:") : [qa.a];
              return (
                <div key={idx} style={{ 
                  background: theme.colors.card, 
                  border: `1px solid ${theme.colors.cardBorder}`, 
                  borderRadius: theme.radii.md, 
                  overflow: "hidden",
                  boxShadow: "0 8px 28px rgba(0,0,0,0.08)",
                  transition: "all 0.3s ease"
                }}>
                  <div
                    data-faq-item
                    onClick={() => toggleItem(idx)}
                    style={{
                      padding: "clamp(16px, 3vw, 20px)",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      fontWeight: 600,
                      fontSize: "clamp(16px, 2.5vw, 18px)",
                      transition: "background-color 0.2s ease",
                      minHeight: "44px"
                    }}
                    onMouseEnter={(e) => {
                      if (window.innerWidth > 768) {
                        e.currentTarget.style.backgroundColor = "#F9FAFB";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <span style={{ lineHeight: 1.5, flex: 1, paddingRight: 12 }}>{qa.q}</span>
                    <span style={{
                      fontSize: 24,
                      color: theme.colors.accent,
                      transition: "transform 0.3s ease",
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                      display: "inline-block",
                      flexShrink: 0
                    }}>
                      ▼
                    </span>
                  </div>
                  {isOpen && (
                    <div style={{
                      padding: "0 clamp(16px, 3vw, 20px) clamp(16px, 3vw, 20px) clamp(16px, 3vw, 20px)",
                      color: theme.colors.muted,
                      lineHeight: 1.6,
                      whiteSpace: "pre-line",
                      animation: "fadeIn 0.3s ease",
                      fontSize: "clamp(15px, 2vw, 16px)"
                    }}>
                      {parts[0].split(/(\*[^*\n]+\*)/g).map((part, i) => {
                        if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
                          return <em key={i} style={{ fontStyle: 'italic' }}>{part.slice(1, -1)}</em>;
                        }
                        return <span key={i}>{part}</span>;
                      })}
                      {hasDisclaimer && (
                        <div style={{
                          marginTop: 12,
                          padding: 12,
                          background: "#DBEAFE",
                          border: "2px solid #93C5FD",
                          borderRadius: 8,
                          color: "#1E40AF",
                          fontWeight: 600,
                          fontSize: "clamp(14px, 2vw, 16px)"
                        }}>
                          BELANGRIJK: {parts[1].replace(/\*\*/g, "")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <style>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </section>
    );
  };

  const isInternal = (href) => href && href.startsWith("/");
  const isHash = (href) => href && href.startsWith("#");
  const smoothScrollTo = (hash) => {
    const el = document.querySelector(hash);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const renderCta = (cta, variant) => {
    const baseStyle = {
      padding: "clamp(12px, 2vw, 14px) clamp(20px, 4vw, 28px)",
      borderRadius: 9999,
      border: `1px solid ${theme.colors.cardBorder}`,
      color: variant === "primary" ? theme.colors.accentContrast : theme.colors.foreground,
      background: "transparent",
      textDecoration: "none",
      display: "inline-block",
      boxShadow: variant === "primary" ? "0 8px 24px rgba(51,153,255,0.35)" : "0 4px 16px rgba(0,0,0,0.06)",
      fontWeight: 600,
      fontSize: "clamp(15px, 2vw, 17px)",
      minHeight: "44px",
      lineHeight: 1.5,
      textAlign: "center",
      boxSizing: "border-box"
    };

    const variants = {
      primary: {
        background: "#FFFFFF",
        border: "1px solid rgba(0,0,0,0.05)",
        color: "#2E2E2E",
        boxShadow: "0 12px 30px rgba(0,0,0,0.12)"
      },
      secondary: { background: theme.colors.card },
      ghost: { background: "rgba(255,255,255,0.15)", color: "#FFFFFF", border: "none" }
    };

    const style = { ...baseStyle, ...(variants[variant] || {}) };

    if (isHash(cta.href)) {
      return (
        <a href={cta.href} onClick={(e) => { e.preventDefault(); smoothScrollTo(cta.href); }} style={style}>{cta.label}</a>
      );
    }

    if (isInternal(cta.href)) {
      return (
        <Link to={cta.href} style={style}>{cta.label}</Link>
      );
    }
    return (
      <a href={cta.href} style={style}>{cta.label}</a>
    );
  };

  return (
    <div style={{ background: "linear-gradient(180deg, #eef4ff 0%, #f5f8ff 20%, #ffffff 45%, #ffffff 100%)", color: theme.colors.foreground, minHeight: "100vh", position: "relative", overflowX: "hidden" }}>
      {/* Mobile Header – same feel as PC: menu hovering on gradient, logo + Get started + hamburger */}
      <div style={{ position: "sticky", top: 0, zIndex: 100, background: "#fff", borderBottom: "1px solid #E5E7EB" }}>
        <header className="mobile-header" style={{
          padding: "16px 20px",
          display: "none",
          alignItems: "center",
          justifyContent: "space-between"
        }}>
          <Link to="/" style={{ display: "flex", alignItems: "center", textDecoration: "none" }} aria-label="Slimmer af">
              {logoNameSvg ? (
                <div style={{ height: 36, width: 135, flexShrink: 0 }} dangerouslySetInnerHTML={{ __html: logoNameSvg }} />
              ) : (
                <span style={{ fontSize: 18, fontWeight: 700, color: "#374151" }}>Slimmer af.</span>
              )}
            </Link>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: "auto", paddingRight: 4 }}>
          <Link
            to="/login"
            style={{
              padding: "10px 16px",
              borderRadius: 9999,
              border: "1px solid #E5E7EB",
              background: "#fff",
              color: "#374151",
              textDecoration: "none",
              fontSize: 14,
              fontWeight: 600,
              whiteSpace: "nowrap"
            }}
          >
            Login
          </Link>
          <Link
            to="/portal-demo"
            style={{
              padding: "10px 18px",
              borderRadius: 9999,
              background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)",
              color: "#FFFFFF",
              textDecoration: "none",
              fontSize: 14,
              fontWeight: 600,
              boxShadow: "0 2px 8px rgba(51,153,255,0.3)",
              whiteSpace: "nowrap"
            }}
          >
            Get started
          </Link>
        </div>
      </header>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .mobile-header {
            display: flex !important;
          }
          .landing-hero-desktop-section {
            display: none !important;
          }
          .landing-hero-mobile-section {
            display: flex !important;
            align-items: flex-start !important;
            justify-content: center !important;
            min-height: calc(100vh - 56px) !important;
            padding: 80px 24px 40px 24px !important;
            box-sizing: border-box !important;
            margin-top: 0 !important;
            margin-bottom: 0 !important;
          }
          .landing-section-padding {
            padding: 48px 20px !important;
          }
          .landing-grid-3 {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
          .landing-grid-auto-fit {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
          .landing-feature-card {
            margin-bottom: 24px !important;
          }
          .landing-text-center-mobile {
            text-align: center !important;
          }
          .landing-full-width-mobile {
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 16px !important;
            box-sizing: border-box !important;
          }
          .landing-hero-cta-container {
            flex-direction: column !important;
            width: 100% !important;
            gap: 12px !important;
          }
          .landing-hero-cta {
            width: 100% !important;
            margin: 0 !important;
          }
          .landing-hero-cta a,
          .landing-hero-cta button {
            width: 100% !important;
            min-height: 48px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            margin: 0 !important;
          }
          section {
            padding: 24px 16px !important;
          }
          #pricing.landing-section-padding {
            padding-top: 32px !important;
          }
          img {
            max-width: 100% !important;
            height: auto !important;
          }
        }
        @media (min-width: 769px) {
          .mobile-header {
            display: none !important;
          }
          .landing-hero-desktop-section {
            display: flex !important;
          }
          .landing-hero-mobile-section {
            display: none !important;
          }
        }
        @media (max-width: 480px) {
          .landing-section-padding {
            padding: 32px 16px !important;
          }
        }
        @keyframes landingMouseSegmentFloat {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(6px, -5px); }
          50% { transform: translate(-4px, 6px); }
          75% { transform: translate(5px, 4px); }
        }
        img.mouse-segment-float {
          animation: landingMouseSegmentFloat 5s ease-in-out infinite;
        }
      `}</style>
      {/* Main content */}
      <main>
        {sections.map((s, idx) => (
          <Section key={s.id || idx} section={s} />
        ))}
      </main>

      <footer style={{ borderTop: `1px solid ${theme.colors.cardBorder}`, padding: "clamp(32px, 5vw, 48px) 16px", marginTop: "4px", background: "transparent", color: theme.colors.muted }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "8px 24px", textAlign: "center" }}>
          {footer.columns.map((col, idx) => (
            <div key={idx} style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", justifyContent: "center", gap: "8px 24px", alignItems: "center" }}>
              {col.title ? <div style={{ color: theme.colors.foreground, fontWeight: 600, marginRight: 8, fontSize: "clamp(14px, 2vw, 16px)" }}>{col.title}</div> : null}
              {col.links.map((l, lidx) => (
                l.href.startsWith("/") ? (
                  <Link key={lidx} to={l.href} style={{ color: theme.colors.muted, textDecoration: "none", fontSize: "clamp(13px, 2vw, 14px)", lineHeight: 1.5, display: "inline-flex", alignItems: "center" }}>{l.label}</Link>
                ) : (
                  <a key={lidx} href={l.href} style={{ color: theme.colors.muted, textDecoration: "none", fontSize: "clamp(13px, 2vw, 14px)", lineHeight: 1.5, display: "inline-flex", alignItems: "center" }}>{l.label}</a>
                )
              ))}
            </div>
          ))}
        </div>
        <div style={{ maxWidth: 1200, margin: "16px auto 0", fontSize: "clamp(11px, 1.5vw, 12px)", textAlign: "center", lineHeight: 1.5 }}>
          {footer.note}{new Date().getFullYear()} Slimmer af All rights reserved.
        </div>
      </footer>
    </div>
  );
}

function InvestmentSection({ section }) {
  const handleFaqLink = (e) => {
    e.preventDefault();
    const faqSection = document.getElementById('faq');
    if (faqSection) {
      faqSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Open the second FAQ item (index 1) about investing after a short delay
      setTimeout(() => {
        const faqItems = faqSection.querySelectorAll('[data-faq-item]');
        if (faqItems.length > 1) {
          faqItems[1].click();
        }
      }, 500);
    }
  };

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          #${section.id} {
            padding: 0px 24px 40px !important;
            margin-top: -40px !important;
          }
        }
      `}</style>
      <div id={section.id} className="landing-section-padding" style={{ padding: "100px 24px 140px", background: "#fff" }}>
      <div className="landing-full-width-mobile" style={{ maxWidth: 1400, margin: "0 auto" }}>
        <div className="landing-text-center-mobile" style={{ textAlign: "center", marginBottom: 60 }}>
          <h2 style={{ fontSize: "clamp(1.5rem, 5vw, 3rem)", fontWeight: 700, marginBottom: 20, color: "#1f2937" }}>
            {section.title}
          </h2>
          <p style={{ fontSize: "clamp(16px, 2.5vw, 21px)", color: "#6b7280", maxWidth: 900, margin: "0 auto", lineHeight: 1.65 }}>
            {section.content}
          </p>
        </div>
        
        <div className="landing-grid-3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 32, maxWidth: 1300, margin: "0 auto" }}>
          {section.options?.map((option, index) => (
            <div key={index} style={{
              padding: "24px 24px",
              background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              borderRadius: 20,
              border: "none",
              boxShadow: "0 12px 34px rgba(0,0,0,0.1)",
              transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              backfaceVisibility: "hidden",
              cursor: "pointer"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translate3d(0, -8px, 0)";
              e.currentTarget.style.boxShadow = "0 22px 52px rgba(51,153,255,0.28)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translate3d(0, 0, 0)";
              e.currentTarget.style.boxShadow = "0 12px 34px rgba(0,0,0,0.1)";
            }}>
              <div style={{
                width: 56,
                height: 56,
                background: "rgba(255,255,255,0.2)",
                borderRadius: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 0 16px 0",
                fontSize: 30
              }}>
                {option.icon}
              </div>
              <h3 style={{ fontSize: "clamp(18px, 2.5vw, 20px)", fontWeight: 700, marginBottom: 10, color: "#FFFFFF", lineHeight: 1.3, margin: "0 0 10px 0" }}>
                {option.title}
              </h3>
              {option.description && option.description.trim() !== "" && (
                <p 
                  style={{ fontSize: "clamp(15px, 2vw, 16px)", color: "rgba(255,255,255,0.9)", margin: 0, lineHeight: 1.5 }}
                  onClick={(e) => {
                    if (e.target.tagName === 'A') {
                      e.preventDefault();
                      const faqSection = document.getElementById('faq');
                      if (faqSection) {
                        faqSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        setTimeout(() => {
                          const faqItems = faqSection.querySelectorAll('[data-faq-item]');
                          if (faqItems.length > 1) {
                            faqItems[1].click();
                          }
                        }, 500);
                      }
                    }
                  }}
                  dangerouslySetInnerHTML={{ __html: option.description }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
    </>
  );
}

function StorySection({ id, text, theme, frame }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const charNodes = Array.from(el.querySelectorAll('[data-char]'));

    // Determine reading order: row by row (top-to-bottom), left-to-right
    const ordered = charNodes
      .map((node, idx) => {
        const rect = node.getBoundingClientRect();
        return { node, idx, top: Math.round(rect.top), left: Math.round(rect.left) };
      })
      .sort((a, b) => (a.top === b.top ? a.left - b.left : a.top - b.top))
      .map((o) => o.node);

    const initialGrey = theme.colors.background || "#e5e3e3";
    const finalGrey = "#2e2e2e";

    // Initialize all chars to background grey
    ordered.forEach((n) => {
      n.style.color = initialGrey;
    });

    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 0;
      // Slower reveal: extend the window from when bottom enters (95% vh) until top passes -20% vh
      const start = vh * 0.95;
      const end = -vh * 0.2;
      const t = (start - rect.top) / (start - end);
      const progress = Math.max(0, Math.min(1, t));

      const revealCount = Math.floor(progress * ordered.length);
      for (let i = 0; i < ordered.length; i++) {
        ordered[i].style.color = i < revealCount ? finalGrey : initialGrey;
      }
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [theme.colors.background]);

  const renderChars = (str) => (
    str.split("").map((ch, i) => {
      if (ch === "\n") return <br key={`br-${i}`} />;
      return <span key={i} data-char style={{ transition: "color 550ms ease" }}>{ch}</span>;
    })
  );

  const heightCalc = `calc(100vh - ${frame.top * 2}px)`;

  return (
    <section id={id} style={{ padding: 0, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
      <div style={{ 
        width: "100%", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center",
        padding: `${frame.top}px ${typeof frame.side === 'number' ? frame.side + 'vw' : frame.side}`,
        boxSizing: "border-box"
      }}>
        <div ref={containerRef} style={{
          position: "relative",
          borderRadius: 28,
          overflow: "hidden",
          boxShadow: "0 50px 140px rgba(0,0,0,0.25)",
          width: "100%",
          height: heightCalc,
          minHeight: "400px",
          maxWidth: 1800,
          background: theme.colors.card,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box"
        }}>
        <div style={{ padding: "clamp(20px, 8vw, 100px)", maxWidth: "100%" }}>
          <h2 style={{ margin: 0, fontSize: "clamp(1.5rem, 5vw, 3.25rem)", lineHeight: 1.3, color: theme.colors.foreground }}>{renderChars(text)}</h2>
        </div>
        </div>
      </div>
    </section>
  );
}
